import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronDown, CircleHelp, Lock, Sparkle } from "lucide-react";
import { Rule, roman } from "./Ornament";
import { WaxSeal } from "./WaxSeal";
import { createController, nodeById, DOMAINS, domainLabel, listWords, ITEMS, supportersFor } from "@/lib/catalog";

const Ticks = ({ asked, min, max }) => (
  <div className="flex items-center gap-1.5" data-testid="diag-progress-ticks">
    {Array.from({ length: max }).map((_, i) => (
      <span
        key={i}
        className={`h-1.5 w-1.5 rounded-full transition-colors ${
          i < asked ? "bg-primary" : i === asked ? "bg-primary/60 ring-2 ring-primary/25" : "bg-border"
        } ${i === min - 1 ? "outline outline-1 outline-dashed outline-muted-foreground/50 outline-offset-1" : ""}`}
      />
    ))}
  </div>
);

const Readout = ({ route, result, history, onStart, onBack }) => {
  const ids = route.steps.map((s) => s.id);
  const verified = ids.filter((id) => result.verified.includes(id));
  const solid = ids.filter((id) => result.inferredReady.includes(id) && !result.verified.includes(id));
  const startIdx = Math.max(0, ids.findIndex((id) => !result.verified.includes(id)));
  const allVerified = ids.every((id) => result.verified.includes(id));
  const start = route.steps[allVerified ? ids.length - 1 : startIdx];
  const [openId, setOpenId] = useState(null);

  const trace = (id, kind) => {
    const belief = result.belief?.get?.(id);
    const pct = belief != null ? `${Math.round(belief * 100)}%` : null;
    if (kind === "verified") {
      const h = history.find((x) => x.nodeId === id && x.outcome === "correct");
      return (
        <>
          <span className="eyebrow text-primary block">Verified · probe answered correctly</span>
          {h && <p className="mt-1.5 font-display text-sm leading-snug">“{ITEMS[id]?.q}”</p>}
          {pct && <p className="mt-1 text-xs text-muted-foreground">Belief {pct}</p>}
        </>
      );
    }
    const sup = supportersFor(id, history).map((s) => nodeById[s].name);
    return (
      <>
        <span className="eyebrow text-[hsl(var(--soft))] block">Inferred · not asked</span>
        <p className="mt-1.5 text-sm leading-snug">
          {sup.length
            ? `Propagated from correct answers on ${listWords(sup)}, which depend on this step.`
            : "Belief rose above the confirm floor from neighbouring answers, but no downstream probe was answered correctly."}
        </p>
        {pct && <p className="mt-1 text-xs text-muted-foreground">Belief {pct} · below the verified bar, so not sealed</p>}
      </>
    );
  };

  const Tier = ({ label, note, items, kind, testid, icon }) => (
    <div className="card-ink p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="eyebrow text-primary">{label}</span>
        <span className="eyebrow text-muted-foreground ml-auto">{items.length}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{note}</p>
      {items.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {items.map((id) => {
            const isOpen = openId === id;
            return (
              <li key={id} className={isOpen ? "basis-full" : ""}>
                <button
                  data-testid={`verdict-${id}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenId(isOpen ? null : id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-display transition-colors ${isOpen ? "border-primary/60 text-primary" : "border-border bg-background/60 hover:border-primary/40"}`}
                >
                  {nodeById[id].name}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div data-testid={`verdict-trace-${id}`} className="mt-2 rounded-lg border border-border bg-background/60 p-3 atlas-rise">
                    {trace(id, kind)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
  return (
    <section data-testid="diagnostic-readout" className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-24 atlas-rise">
      <Rule label="Fix acquired" className="max-w-xs mx-auto mb-5" />
      <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl leading-[1.05] text-center">
        Where you’re standing on <span className="italic text-primary">this path</span>
      </h2>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        {result.asked.length} questions asked · only the instrumented steps were tested.
      </p>
      <div className="mt-8 space-y-3">
        <Tier testid="readout-verified" label="Locked in" kind="verified" icon={<WaxSeal className="h-5 w-5" />} items={verified}
          note="Asked and confirmed. These steps are sealed on your route. Tap a step to see the probe." />
        <Tier testid="readout-solid" label="Looks solid — worth a quick check" kind="inferred" icon={<Sparkle className="h-4 w-4 text-primary" />} items={solid}
          note="Inferred from what you got right. Not marked complete — a hint, not a verdict. Tap a step to see why." />
        <div className="plate-frame card-ink bg-secondary/40 p-5 sm:p-6" data-testid="readout-start">
          <span className="eyebrow text-primary">Start here</span>
          <p className="font-display text-2xl mt-1">{start.title}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {allVerified
              ? "Every instrumented step checked out. Begin at the destination."
              : `Step ${roman(startIdx + 1)} of ${roman(ids.length)} — the first step Atlas couldn’t verify.`}
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button data-testid="primary-next-step-cta" onClick={() => onStart(allVerified ? ids.length - 1 : startIdx, verified, solid)} className="btn-brass">
              Start at “{start.title}” <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="readout-back-button" onClick={() => onBack(verified, solid)} className="btn-ghost">
              Back to route
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Diagnostic = ({ route, probe, onFinish, onBack }) => {
  const ctrl = useMemo(() => createController(route.targets), [route.targets]);
  const [q, setQ] = useState(() => ctrl.start());
  const [result, setResult] = useState(null);

  const pick = (idx) => {
    const next = ctrl.answer(idx);
    if (next) setQ(next);
    else setResult(ctrl.result());
  };

  if (result) return <Readout route={route} result={result} history={ctrl.history()} onStart={(i, v, s) => onFinish(i, v, s)} onBack={(v, s) => onBack(v, s)} />;
  if (!q) return null;

  const node = nodeById[q.nodeId];
  const dom = DOMAINS[node.cat];
  const domains = listWords(probe.domains.map(domainLabel));

  return (
    <section data-testid="diagnostic-container" className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-20 atlas-rise">
      <button data-testid="diagnostic-back-button" onClick={() => onBack([], [])} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to route
      </button>

      <div className="flex items-center justify-between gap-4">
        <span className="eyebrow text-primary">Acquiring a fix · {domains}</span>
        <Ticks asked={q.progress.asked} min={q.progress.min} max={q.progress.max} />
      </div>
      <p className="eyebrow text-muted-foreground mt-2" data-testid="diag-progress-note">
        Question {roman(q.progress.asked + 1)} · stops between {q.progress.min} and {q.progress.max}
      </p>

      <div className="plate-frame card-ink mt-6 p-6 sm:p-8" key={q.nodeId}>
        <span className="inline-flex items-center gap-2 eyebrow mb-4" style={{ color: dom?.c }} data-testid="diag-domain">
          <span className="h-2 w-2 rounded-full" style={{ background: dom?.c }} /> {node.name}
        </span>
        <p data-testid="diag-question" className="font-display text-xl sm:text-2xl leading-snug">{q.question}</p>
        <div className="mt-6 grid gap-2.5">
          {q.options.map((opt, i) => (
            <button
              key={i}
              data-testid={`diag-option-${i}`}
              onClick={() => pick(i)}
              className="btn-ghost justify-start text-left font-normal text-base px-4 py-3 gap-3"
            >
              <span className="numeral text-primary text-sm w-5 shrink-0">{String.fromCharCode(65 + i)}.</span>
              <span>{opt}</span>
            </button>
          ))}
          <button data-testid="diag-option-idk" onClick={() => pick(null)} className="mt-1 inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-2">
            <CircleHelp className="h-4 w-4" /> Not sure — skip
          </button>
        </div>
      </div>
      <p className="mt-5 text-xs text-muted-foreground flex items-start gap-2">
        <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        One probe per instrumented step. Committed answers weigh more than “not sure”. Nothing here is graded — it only places you.
      </p>
    </section>
  );
};
